// Чистые правила экрана подписки после переезда продажи в IEC ID.
//
// Здесь нет ни React, ни сети — только решения, которые легко перепутать и
// дорого перепутать:
//   * что именно показывать про действующее право (источник, срок, продление);
//   * жива ли ЛОКАЛЬНАЯ подписка — от этого зависит, прятать ли кнопку покупки
//     (их чекаут о нашей кассе не знает, и человек заплатил бы дважды);
//   * доехала ли оплата после возврата из их витрины — токена заказа в возврате
//     нет и не будет (гайд §4.5), поэтому решение принимается по ПРАВАМ.
//
// Файл — единственный владелец этих правил: вторая копия в компоненте
// разъедется первой же правкой.

import type { EntitlementsGetOutput, IecEntitlementItem } from "@studyhub/shared/contracts/iec-id";

export type Tier = "free" | "plus" | "pro";

const TIER_RANK: Record<Tier, number> = { free: 0, plus: 1, pro: 2 };

/** `plus` ≥ `free`. Нужен и для «уровень уже есть», и для выбора кнопок покупки. */
export function tierAtLeast(tier: Tier, min: Tier): boolean {
	return TIER_RANK[tier] >= TIER_RANK[min];
}

/**
 * Право, определяющее уровень: чем показывать состояние на экране.
 *
 * `null` — экосистема ничего не добавляет (`serviceTier === "free"` либо снимка
 * ещё не было). Это НЕ повод пугать человека: локальную подписку наши права не
 * отменяют, а `trial_expired` просто возвращает на бесплатный тариф.
 */
export interface GoverningEntitlement {
	/** `subscription` | `trial` | `programme_gift` | `admin_grant` | … (набор ИХ). */
	source: string;
	tier: Tier;
	expiresAt: string | null;
	/**
	 * `undefined` = «не знаем»: поля может не быть в ответе API (открытый вопрос
	 * N-10 гайда). Пугать «продлите, иначе доступ закончится» можно ТОЛЬКО по
	 * явному `false` с известной датой.
	 */
	renews: boolean | undefined;
	/** Право экосистемы (IEC+), а не подписка на StudyHub. */
	ecosystemWide: boolean;
}

/**
 * Выбирает право, которым объясняется текущий уровень.
 *
 * Берём записи с уровнем, равным `serviceTier` (именно он — наш уровень по
 * §4.4), из них — самую позднюю по сроку; бессрочная (`expiresAt: null`) важнее
 * любой датированной. При равенстве предпочитаем право экосистемы: подпись
 * «уже включено в IEC+» точнее, чем «подписка».
 *
 * Ограничение, которое честнее назвать, чем спрятать: `serviceId` чужого
 * сервиса клиенту неотличим от нашего (своего `service_id` фронт не знает), и
 * запись другого сервиса с тем же уровнем теоретически может выиграть отбор.
 * На решение это не влияет — доступ считает `serviceTier`, посчитанный ими для
 * нас; расходится только подпись срока.
 */
export function resolveGoverningEntitlement(
	items: readonly IecEntitlementItem[],
	serviceTier: Tier,
): GoverningEntitlement | null {
	if (serviceTier === "free") return null;

	const candidates = items.filter((item) => item.tier === serviceTier);
	if (candidates.length === 0) return null;

	const best = candidates.reduce((acc, item) => (isBetterCandidate(item, acc) ? item : acc));
	return {
		source: best.source,
		tier: best.tier,
		expiresAt: best.expiresAt,
		renews: best.renews,
		ecosystemWide: best.serviceId === null,
	};
}

function isBetterCandidate(next: IecEntitlementItem, current: IecEntitlementItem): boolean {
	// Бессрочное право старше любого датированного.
	if (next.expiresAt === null && current.expiresAt !== null) return true;
	if (next.expiresAt !== null && current.expiresAt === null) return false;
	if (next.expiresAt !== null && current.expiresAt !== null) {
		const diff = Date.parse(next.expiresAt) - Date.parse(current.expiresAt);
		if (diff !== 0) return diff > 0;
	}
	// Сроки равны — право экосистемы объясняет уровень точнее.
	return next.serviceId === null && current.serviceId !== null;
}

/** Форма локальной подписки, которая нужна экрану. Полный тип — в контракте billing. */
export interface LocalSubscriptionSnapshot {
	status: "trial" | "active" | "past_due" | "canceled" | "paused";
	currentPeriodEnd: string;
	cancelAtPeriodEnd: boolean;
}

/**
 * Жива ли локальная подписка.
 *
 * `canceled` / `paused` — уже не дают доступа, но `canceled` до конца
 * оплаченного периода ещё живёт: пока не наступил `currentPeriodEnd`, человек
 * оплачен, и предлагать ему купить у них — предлагать вторую оплату.
 */
export function isLocalSubscriptionAlive(
	subscription: LocalSubscriptionSnapshot | null | undefined,
	now: number = Date.now(),
): boolean {
	if (!subscription) return false;
	if (subscription.status === "trial" || subscription.status === "active") return true;
	if (subscription.status === "past_due") return true;
	if (subscription.status === "canceled") return Date.parse(subscription.currentPeriodEnd) > now;
	return false;
}

/**
 * Что предлагать человеку на экране подписки.
 *
 * Три взаимоисключающих исхода, и порядок проверок — не косметика:
 *   1. `included` — уровень уже дала экосистема. Купить отдельную подписку
 *      поверх действующего IEC+ нельзя: их оформление ответит конфликтом
 *      «IEC+ уже включает этот сервис» (§4.5). Кнопки покупки быть не должно.
 *   2. `local` — жива наша локальная подписка. Их чекаут о ней не знает —
 *      покупка стала бы второй оплатой за то же самое.
 *   3. `buy` — покупать можно.
 */
export type PurchaseState = "included" | "local" | "buy";

export function resolvePurchaseState(args: {
	serviceTier: Tier;
	localAlive: boolean;
}): PurchaseState {
	if (args.serviceTier !== "free") return "included";
	if (args.localAlive) return "local";
	return "buy";
}

/**
 * Показывать ли предложение пробного.
 *
 * `trial.available` считают ОНИ под свою политику, и четыре причины отказа
 * (политики нет, выключена, израсходован, уровень уже есть) наружу не
 * различаются намеренно. Наше единственное добавление — локальная подписка: их
 * ответ про неё не знает (§4.6), а предлагать пробный оплатившему нельзя.
 */
export function shouldOfferTrial(args: {
	trial: EntitlementsGetOutput["trial"];
	localAlive: boolean;
}): boolean {
	if (args.localAlive) return false;
	const trial = args.trial;
	return Boolean(trial?.available && trial.days !== null && trial.days > 0);
}

// ─────────────────────────────────────────────────────────────────────────────
// Экран возврата из их витрины (§4.5 шаг 3)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Снимок уровня НА МОМЕНТ ухода на оплату.
 *
 * Нужен потому, что возврат не несёт ни токена заказа, ни признака успеха: без
 * точки отсчёта «право уже есть» неотличимо от «право только что купили», и
 * быстрый вебхук (право доехало раньше браузера) навсегда оставил бы экран в
 * «проверяем». Снимок живёт в `sessionStorage` — переживает уход на их домен и
 * возврат, но не переезжает в другую вкладку и не гниёт между сессиями.
 */
export interface CheckoutBaseline {
	tier: Tier;
	serviceTier: Tier;
	at: number;
}

export type ReturnOutcome = "pending" | "confirmed" | "timeout";

/**
 * Доехало ли право.
 *
 * Без снимка (человек вернулся в другой вкладке / на другом устройстве)
 * подтверждением считается сам факт непустого права от экосистемы — это
 * слабее, но честнее вечного спиннера.
 */
export function resolveReturnOutcome(args: {
	baseline: CheckoutBaseline | null;
	current: { tier: Tier; serviceTier: Tier } | undefined;
	attempts: number;
	maxAttempts: number;
}): ReturnOutcome {
	const { baseline, current, attempts, maxAttempts } = args;
	if (current) {
		const changed = baseline
			? current.serviceTier !== baseline.serviceTier || current.tier !== baseline.tier
			: current.serviceTier !== "free";
		if (changed) return "confirmed";
	}
	return attempts >= maxAttempts ? "timeout" : "pending";
}
